// frontend/src/modules/empresas/pages/EditarEmpresa.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSave, FaTimes, FaUserPlus, FaEdit } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || '';

function EditarEmpresa() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [empresa, setEmpresa] = useState({
    tipo_empresa: '',
    natureza_juridica: '',
    nome_empresa: '',
    nome_fantasia: '',
    cnpj: '',
    data_abertura: '',
    inscricao_estadual: '',
    inscricao_municipal: '',
    regime_tributario: 'SIMPLES_NACIONAL',
    capital_social: 0,
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    pais: 'Brasil',
    telefone: '',
    email: '',
    site: '',
    cnae_principal: '',
    cnae_descricao: '',
    ativo: true
  });

  const [usuarios, setUsuarios] = useState([]);
  const [mostrarFormUsuario, setMostrarFormUsuario] = useState(false);
  const [editandoUsuario, setEditandoUsuario] = useState(null);
  const [usuarioForm, setUsuarioForm] = useState({
    nome_completo: '',
    email: '',
    senha: '',
    perfil: 'USUARIO',
    role: 'USER'
  });

  const token = localStorage.getItem('token');

  // ============================================
  // BUSCAR EMPRESA
  // ============================================
  const fetchEmpresa = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/empresas/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro:', errorText);
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      setEmpresa({
        ...data,
        data_abertura: data.data_abertura ? data.data_abertura.split('T')[0] : '',
        capital_social: data.capital_social || 0
      });
    } catch (err) {
      console.error('❌ Erro detalhado:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // BUSCAR USUÁRIOS
  // ============================================
  const fetchUsuarios = async () => {
    try {
      const response = await fetch(`${API_URL}/api/usuarios/empresa/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro:', errorText);
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      setUsuarios(data);
    } catch (err) {
      console.error('❌ Erro ao buscar usuários:', err);
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchEmpresa();
    fetchUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEmpresa({
      ...empresa,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleUsuarioChange = (e) => {
    const { name, value } = e.target;
    setUsuarioForm({ ...usuarioForm, [name]: value });
  };

  // ============================================
  // ATUALIZAR EMPRESA
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/empresas/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(empresa)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao atualizar empresa');
      }

      setSuccess('✅ Empresa atualizada com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // CRIAR/ATUALIZAR USUÁRIO
  // ============================================
  const handleCriarUsuario = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const url = editandoUsuario
        ? `${API_URL}/api/usuarios/${editandoUsuario.id}`
        : `${API_URL}/api/usuarios`;

      const method = editandoUsuario ? 'PUT' : 'POST';

      const payload = {
        ...usuarioForm,
        empresa_id: parseInt(id)
      };

      if (!editandoUsuario && !usuarioForm.senha) {
        throw new Error('Senha é obrigatória para novo usuário');
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao salvar');
      }

      setSuccess(editandoUsuario ? '✅ Usuário atualizado!' : '✅ Usuário criado!');
      setUsuarioForm({ nome_completo: '', email: '', senha: '', perfil: 'USUARIO', role: 'USER' });
      setEditandoUsuario(null);
      setMostrarFormUsuario(false);
      fetchUsuarios();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditUsuario = (usuario) => {
    setEditandoUsuario(usuario);
    setUsuarioForm({
      nome_completo: usuario.nome_completo,
      email: usuario.email,
      senha: '',
      perfil: usuario.perfil || 'USUARIO',
      role: usuario.role || 'USER'
    });
    setMostrarFormUsuario(true);
  };

  // ============================================
  // ATUALIZAR STATUS DO USUÁRIO
  // ============================================
  const handleToggleUsuario = async (usuarioId, ativo) => {
    try {
      const response = await fetch(`${API_URL}/api/usuarios/${usuarioId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ativo: !ativo })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao atualizar usuário');
      }

      fetchUsuarios();
      alert(`Usuário ${!ativo ? 'ativado' : 'desativado'} com sucesso!`);
    } catch (err) {
      setError(err.message);
    }
  };

  // ============================================
  // ESTILOS
  // ============================================
  const styles = {
    container: {
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '20px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '10px'
    },
    title: {
      fontSize: 'clamp(1.2rem, 4vw, 2rem)',
      margin: 0
    },
    backButton: {
      padding: '8px 16px',
      backgroundColor: '#6c757d',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: 'clamp(0.8rem, 1.5vw, 1rem)'
    },
    formCard: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      marginBottom: '30px'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '15px'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column'
    },
    label: {
      fontWeight: '500',
      marginBottom: '5px',
      fontSize: 'clamp(0.85rem, 1.2vw, 1rem)'
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: 'clamp(0.85rem, 1.2vw, 1rem)'
    },
    select: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: 'clamp(0.85rem, 1.2vw, 1rem)',
      backgroundColor: 'white'
    },
    checkboxGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginTop: '8px'
    },
    checkbox: {
      width: '20px',
      height: '20px',
      cursor: 'pointer'
    },
    saveButton: {
      marginTop: '20px',
      padding: '10px 30px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: 'clamp(0.9rem, 1.2vw, 1rem)'
    },
    errorMessage: {
      backgroundColor: '#ffebee',
      padding: '10px 15px',
      borderRadius: '4px',
      color: '#c62828',
      marginBottom: '15px',
      fontSize: 'clamp(0.85rem, 1.2vw, 1rem)'
    },
    successMessage: {
      backgroundColor: '#e8f5e9',
      padding: '10px 15px',
      borderRadius: '4px',
      color: '#2e7d32',
      marginBottom: '15px',
      fontSize: 'clamp(0.85rem, 1.2vw, 1rem)'
    },
    usuariosSection: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    usuariosHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '10px'
    },
    newUserButton: {
      padding: '8px 16px',
      backgroundColor: '#28a745',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: 'clamp(0.8rem, 1.2vw, 1rem)'
    },
    usuarioForm: {
      backgroundColor: '#f8f9fa',
      padding: '15px',
      borderRadius: '8px',
      marginBottom: '20px'
    },
    tableWrapper: {
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '600px'
    },
    th: {
      padding: '10px',
      textAlign: 'left',
      borderBottom: '2px solid #dee2e6',
      backgroundColor: '#f8f9fa',
      fontWeight: '600',
      fontSize: 'clamp(0.8rem, 1.1vw, 0.95rem)'
    },
    td: {
      padding: '10px',
      borderBottom: '1px solid #dee2e6',
      fontSize: 'clamp(0.8rem, 1.1vw, 0.95rem)'
    },
    statusBadge: {
      padding: '3px 10px',
      borderRadius: '12px',
      display: 'inline-block',
      fontSize: 'clamp(0.7rem, 1vw, 0.85rem)',
      whiteSpace: 'nowrap'
    },
    actionButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '5px',
      fontSize: 'clamp(0.9rem, 1.2vw, 1rem)'
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '50px',
      fontSize: 'clamp(1rem, 2vw, 1.5rem)'
    },
    emptyState: {
      textAlign: 'center',
      padding: '20px',
      color: '#6c757d'
    }
  };

  if (loading) {
    return <div style={styles.loadingContainer}>🔄 Carregando...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>✏️ Editar Empresa</h2>
        <button onClick={() => navigate('/empresas')} style={styles.backButton}>
          <FaTimes /> Voltar
        </button>
      </div>

      {error && <div style={styles.errorMessage}>❌ {error}</div>}
      {success && <div style={styles.successMessage}>✅ {success}</div>}

      <form onSubmit={handleSubmit} style={styles.formCard}>
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>📋 Dados da Empresa</h3>
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Tipo de Empresa</label>
            <select name="tipo_empresa" value={empresa.tipo_empresa || ''} onChange={handleChange} style={styles.select}>
              <option value="">Selecione...</option>
              <option value="MEI">MEI</option>
              <option value="EI">Empresário Individual</option>
              <option value="EIRELI">EIRELI</option>
              <option value="LTDA">Sociedade Limitada</option>
              <option value="SA">Sociedade Anônima</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Natureza Jurídica</label>
            <input name="natureza_juridica" value={empresa.natureza_juridica || ''} onChange={handleChange} placeholder="Ex: Sociedade Empresária Limitada" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Nome Fantasia *</label>
            <input name="nome_fantasia" value={empresa.nome_fantasia || ''} onChange={handleChange} required style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Razão Social *</label>
            <input name="nome_empresa" value={empresa.nome_empresa || ''} onChange={handleChange} required style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>CNPJ *</label>
            <input name="cnpj" value={empresa.cnpj || ''} onChange={handleChange} required style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Data de Abertura</label>
            <input name="data_abertura" value={empresa.data_abertura || ''} onChange={handleChange} type="date" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Inscrição Estadual</label>
            <input name="inscricao_estadual" value={empresa.inscricao_estadual || ''} onChange={handleChange} placeholder="Número da inscrição estadual" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Inscrição Municipal</label>
            <input name="inscricao_municipal" value={empresa.inscricao_municipal || ''} onChange={handleChange} placeholder="Número da inscrição municipal" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Regime Tributário</label>
            <select name="regime_tributario" value={empresa.regime_tributario || 'SIMPLES_NACIONAL'} onChange={handleChange} style={styles.select}>
              <option value="SIMPLES_NACIONAL">Simples Nacional</option>
              <option value="LUCRO_PRESUMIDO">Lucro Presumido</option>
              <option value="LUCRO_REAL">Lucro Real</option>
              <option value="MEI">MEI</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Capital Social (R$)</label>
            <input name="capital_social" value={empresa.capital_social || ''} onChange={handleChange} type="number" step="0.01" placeholder="0,00" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <div style={styles.checkboxGroup}>
              <label style={styles.label}>Ativo</label>
              <input name="ativo" checked={empresa.ativo !== false} onChange={handleChange} type="checkbox" style={styles.checkbox} />
            </div>
          </div>
        </div>

        <h3 style={{ marginTop: '25px', marginBottom: '20px' }}>📍 Endereço</h3>
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>CEP</label>
            <input name="cep" value={empresa.cep || ''} onChange={handleChange} placeholder="00000-000" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Logradouro</label>
            <input name="logradouro" value={empresa.logradouro || ''} onChange={handleChange} placeholder="Rua, Avenida..." style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Número</label>
            <input name="numero" value={empresa.numero || ''} onChange={handleChange} placeholder="Número" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Complemento</label>
            <input name="complemento" value={empresa.complemento || ''} onChange={handleChange} placeholder="Sala, Bloco, ..." style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Bairro</label>
            <input name="bairro" value={empresa.bairro || ''} onChange={handleChange} placeholder="Bairro" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Cidade</label>
            <input name="cidade" value={empresa.cidade || ''} onChange={handleChange} placeholder="Cidade" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Estado</label>
            <input name="estado" value={empresa.estado || ''} onChange={handleChange} placeholder="Estado" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>País</label>
            <input name="pais" value={empresa.pais || 'Brasil'} onChange={handleChange} style={styles.input} />
          </div>
        </div>

        <h3 style={{ marginTop: '25px', marginBottom: '20px' }}>📞 Contato</h3>
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Telefone</label>
            <input name="telefone" value={empresa.telefone || ''} onChange={handleChange} placeholder="(11) 99999-9999" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email *</label>
            <input name="email" value={empresa.email || ''} onChange={handleChange} required type="email" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Site</label>
            <input name="site" value={empresa.site || ''} onChange={handleChange} placeholder="www.empresa.com" style={styles.input} />
          </div>
        </div>

        <h3 style={{ marginTop: '25px', marginBottom: '20px' }}>📊 CNAE</h3>
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>CNAE Principal</label>
            <input name="cnae_principal" value={empresa.cnae_principal || ''} onChange={handleChange} placeholder="Ex: 6201-5/01" style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Descrição da Atividade</label>
            <input name="cnae_descricao" value={empresa.cnae_descricao || ''} onChange={handleChange} placeholder="Descrição da atividade principal" style={styles.input} />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          style={{
            ...styles.saveButton,
            opacity: saving ? 0.7 : 1,
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Salvando...' : <><FaSave /> Salvar Empresa</>}
        </button>
      </form>

      <div style={styles.usuariosSection}>
        <div style={styles.usuariosHeader}>
          <h3 style={{ margin: 0 }}>👥 Usuários da Empresa</h3>
          <button
            onClick={() => {
              setMostrarFormUsuario(!mostrarFormUsuario);
              setEditandoUsuario(null);
              setUsuarioForm({ nome_completo: '', email: '', senha: '', perfil: 'USUARIO', role: 'USER' });
            }}
            style={styles.newUserButton}
          >
            <FaUserPlus /> {mostrarFormUsuario ? 'Cancelar' : 'Novo Usuário'}
          </button>
        </div>

        {mostrarFormUsuario && (
          <form onSubmit={handleCriarUsuario} style={styles.usuarioForm}>
            <h4 style={{ marginTop: 0, marginBottom: '15px' }}>
              {editandoUsuario ? '✏️ Editar Usuário' : '📝 Novo Usuário'}
            </h4>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Nome Completo *</label>
                <input name="nome_completo" value={usuarioForm.nome_completo} onChange={handleUsuarioChange} required style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email *</label>
                <input name="email" value={usuarioForm.email} onChange={handleUsuarioChange} required type="email" style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Senha {editandoUsuario ? '(deixe em branco para manter)' : '*'}
                </label>
                <input name="senha" value={usuarioForm.senha} onChange={handleUsuarioChange} type="password" required={!editandoUsuario} style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Perfil</label>
                <select name="perfil" value={usuarioForm.perfil} onChange={handleUsuarioChange} style={styles.select}>
                  <option value="USUARIO">Usuário Comum</option>
                  <option value="ADMIN">Administrador</option>
                </select>
              </div>
            </div>
            <button
              type="submit"
              disabled={saving}
              style={{
                ...styles.saveButton,
                marginTop: '15px',
                opacity: saving ? 0.7 : 1,
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {editandoUsuario ? '💾 Atualizar' : '💾 Criar'}
            </button>
          </form>
        )}

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Nome</th>
                <th style={styles.th}>Email</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Perfil</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Role</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Status</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map(user => (
                <tr key={user.id}>
                  <td style={styles.td}>{user.nome_completo}</td>
                  <td style={styles.td}>{user.email}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: user.perfil === 'ADMIN' ? '#cce5ff' : '#e9ecef',
                      color: user.perfil === 'ADMIN' ? '#004085' : '#6c757d'
                    }}>
                      {user.perfil || 'USUARIO'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: user.role === 'SUPER_ADMIN' ? '#f8d7da' : '#e9ecef',
                      color: user.role === 'SUPER_ADMIN' ? '#721c24' : '#6c757d'
                    }}>
                      {user.role || 'USER'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: user.ativo ? '#d4edda' : '#f8d7da',
                      color: user.ativo ? '#155724' : '#721c24'
                    }}>
                      {user.ativo ? '✅ Ativo' : '❌ Inativo'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => handleEditUsuario(user)}
                      style={{ ...styles.actionButton, color: '#007bff' }}
                      title="Editar usuário"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleToggleUsuario(user.id, user.ativo)}
                      style={{
                        ...styles.actionButton,
                        color: user.ativo ? '#dc3545' : '#28a745',
                        marginLeft: '5px'
                      }}
                    >
                      {user.ativo ? 'Desativar' : 'Ativar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {usuarios.length === 0 && <p style={styles.emptyState}>📭 Nenhum usuário cadastrado.</p>}
      </div>
    </div>
  );
}

export default EditarEmpresa;